import React from "react";
import { InteractPrompt } from "@/components/game/InteractPrompt";

// ---- tiny product illustrations (physical shop displays) ----
const MiniJar = ({ fill }) => (
  <div className="relative h-8 w-6 sm:h-10 sm:w-7">
    <div className="absolute left-1/2 top-0 z-10 h-1.5 w-5 -translate-x-1/2 rounded-t border border-black/40 bg-[#9C6644]" />
    <div className="absolute bottom-0 h-7 w-6 overflow-hidden rounded-b-[5px] rounded-t-sm border-2 border-white/60 bg-white/25 sm:h-8 sm:w-7">
      <div className="absolute bottom-0 w-full" style={{ height: "62%", background: fill }}>
        <span className="absolute left-1 top-0.5 h-1 w-1 rounded-full bg-white/70" />
        <span className="absolute right-1 top-1.5 h-1 w-1 rounded-full bg-white/50" />
        <span className="absolute left-2 top-2.5 h-1 w-1 rounded-full bg-white/40" />
      </div>
      <div className="absolute left-1 top-1 h-3 w-1 rounded bg-white/40" />
    </div>
  </div>
);
const Bar = ({ c, rot = 0 }) => (
  <div className="h-7 w-3.5 rounded-[2px] border border-black/40 shadow-[1px_1px_0_rgba(0,0,0,.3)] sm:h-9 sm:w-4" style={{ background: c, transform: `rotate(${rot}deg)` }}>
    <div className="mx-auto mt-1 h-[2px] w-2/3 bg-white/40" />
  </div>
);
const Pack = ({ c, w = 18, h = 9, rot = 0 }) => (
  <div className="rounded-[2px] border border-black/40 shadow-[1px_1px_0_rgba(0,0,0,.3)]" style={{ width: w, height: h, background: c, transform: `rotate(${rot}deg)` }}>
    <div className="mx-auto mt-[3px] h-[2px] w-3/4 bg-white/40" />
  </div>
);
const Hang = ({ c }) => (
  <div className="flex flex-col items-center">
    <div className="h-2 w-[1px] bg-black/40" />
    <div className="h-6 w-4 rounded-b-[3px] rounded-t-[1px] border border-black/40 sm:h-7 sm:w-5" style={{ background: c }}>
      <div className="mx-auto mt-1 h-[2px] w-2/3 bg-white/40" />
    </div>
  </div>
);
const Tag = ({ children }) => (
  <span className="rounded-[2px] border border-kirana-brown bg-[#FDF6E3] px-1 text-[6px] font-bold leading-tight text-kirana-red shadow-[1px_1px_0_rgba(0,0,0,.25)] sm:text-[7px]">{children}</span>
);

const DISPLAYS = {
  "candy-jars": (
    <div className="relative flex items-end justify-center gap-1">
      <MiniJar fill="#E76F51" /><MiniJar fill="#E9C46A" /><MiniJar fill="#2A9D8F" />
      <span className="absolute -bottom-0.5 left-0"><Tag>₹1</Tag></span>
    </div>
  ),
  "chocolate-counter": (
    <div className="relative flex items-end justify-center gap-[3px]">
      <Bar c="#5C3A21" rot={-4} /><Bar c="#8B5A2B" rot={2} /><Bar c="#B5651D" rot={-2} /><Bar c="#6B3F1D" rot={5} /><Bar c="#8B5A2B" rot={0} />
      <span className="absolute -bottom-0.5 right-0"><Tag>₹5</Tag></span>
    </div>
  ),
  "biscuit-shelf": (
    <div className="flex items-end justify-center gap-1.5">
      <div className="flex flex-col-reverse gap-[2px]"><Pack c="#D9A441" rot={-2} /><Pack c="#E9C46A" rot={1} /><Pack c="#C77F3A" rot={-1} /></div>
      <div className="flex flex-col-reverse gap-[2px]"><Pack c="#457B9D" w={16} rot={2} /><Pack c="#8AB17D" w={16} rot={-2} /></div>
      <div className="flex flex-col-reverse gap-[2px]"><Pack c="#E63946" w={14} rot={1} /><Pack c="#D9A441" w={14} rot={-1} /></div>
    </div>
  ),
  "chips-wall": (
    <div className="flex items-start justify-center gap-[3px]">
      <Hang c="#E63946" /><Hang c="#F4A261" /><Hang c="#2A9D8F" /><Hang c="#457B9D" /><Hang c="#E76F51" />
    </div>
  ),
  "churan-counter": (
    <div className="relative flex items-end justify-center gap-1.5">
      <MiniJar fill="#6A0572" /><MiniJar fill="#C1440E" />
      <div className="flex flex-col gap-[2px]"><Pack c="#E9C46A" w={12} h={8} rot={-3} /><Pack c="#E76F51" w={12} h={8} rot={3} /></div>
      <span className="absolute -bottom-0.5 right-0"><Tag>50p</Tag></span>
    </div>
  ),
};

const SIGN_COLOR = {
  "candy-jars": "#C1121F",
  "chocolate-counter": "#4A2C17",
  "biscuit-shelf": "#2A7F62",
  "chips-wall": "#E85D2A",
  "churan-counter": "#6A0572",
  "drinks-fridge": "#1D6FB8",
};

// A shelf category rendered as a real physical product display mounted on a wooden shelf.
const ShelfDisplay = ({ zone, unlocked, count }) => {
  if (zone.id === "drinks-fridge") {
    return (
      <div className="relative flex w-24 flex-col items-center sm:w-28">
        <img src="/home/fridge.png" alt="Cold Drinks Fridge" draggable={false}
          className="h-24 w-auto select-none object-contain drop-shadow-[0_5px_5px_rgba(0,0,0,0.4)] sm:h-32" />
        <div className="relative -mt-1.5 rounded-md border-[3px] border-[#3a2617] px-2 py-0.5"
          style={{ background: SIGN_COLOR["drinks-fridge"], boxShadow: "inset 0 2px 0 rgba(255,255,255,0.25), inset 0 -2px 0 rgba(0,0,0,0.3), 0 3px 0 rgba(0,0,0,0.3)" }}>
          <span className="font-signboard block max-w-[6rem] text-center text-[9px] font-bold uppercase leading-tight tracking-wide text-white drop-shadow-[0_1px_0_rgba(0,0,0,0.5)] sm:text-[11px]">{zone.label}</span>
        </div>
        <span className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-kirana-brown bg-kirana-cream px-1 text-[10px] font-bold text-kirana-brown shadow-[1px_1px_0_rgba(0,0,0,0.3)]">{unlocked}/{count}</span>
      </div>
    );
  }
  return (
  <div className="relative flex w-24 flex-col items-center sm:w-28">
    {/* dark recess holding the products (separates them from the wall) */}
    <div className="relative w-full rounded-t-[4px] border-x-[3px] border-t-[3px] border-[#3a2617] bg-[#241610] px-1 pb-0.5 pt-1 shadow-[inset_0_6px_10px_rgba(0,0,0,0.55)]">
      <div className="flex h-11 items-end justify-center sm:h-14">{DISPLAYS[zone.id]}</div>
    </div>
    {/* thick wooden shelf plank with a cast shadow underneath */}
    <div className="h-2.5 w-[112%] rounded-[2px] border-2 border-[#3a2617] bg-gradient-to-b from-[#b07f4e] to-[#7a5230] shadow-[0_4px_0_rgba(0,0,0,0.4)] sm:h-3" />
    {/* hand-painted signboard mounted below */}
    <div
      className="relative mt-1.5 rounded-md border-[3px] border-[#3a2617] px-2 py-0.5"
      style={{ background: SIGN_COLOR[zone.id], boxShadow: "inset 0 2px 0 rgba(255,255,255,0.25), inset 0 -2px 0 rgba(0,0,0,0.3), 0 3px 0 rgba(0,0,0,0.3)" }}
    >
      <span className="absolute left-1 top-1/2 h-1 w-1 -translate-y-1/2 rounded-full bg-black/40" />
      <span className="absolute right-1 top-1/2 h-1 w-1 -translate-y-1/2 rounded-full bg-black/40" />
      <span className="font-signboard block max-w-[6rem] text-center text-[9px] font-bold uppercase leading-tight tracking-wide text-white drop-shadow-[0_1px_0_rgba(0,0,0,0.5)] sm:text-[11px]">
        {zone.label}
      </span>
    </div>
    <span className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-kirana-brown bg-kirana-cream px-1 text-[10px] font-bold text-kirana-brown shadow-[1px_1px_0_rgba(0,0,0,0.3)]">
      {unlocked}/{count}
    </span>
  </div>
  );
};

export const Zone = ({ zone, active, count, unlocked, onClick }) => {
  const Icon = zone.icon;
  const isDoor = zone.type === "door";
  const isNpc = zone.type === "npc";
  const isShelf = zone.type === "shelf";
  return (
    <div
      className="absolute z-20"
      style={{ left: `${zone.x}%`, top: `${zone.y}%`, transform: "translate(-50%, -50%)" }}
    >
      {active && <InteractPrompt />}
      <button
        data-testid={`zone-${zone.id}`}
        onClick={onClick}
        className={`group relative transition-transform duration-150 hover:scale-[1.05] ${active ? "scale-[1.05]" : ""} ${
          isShelf ? (active ? "zone-glow rounded-md" : "") : `flex flex-col items-center gap-0.5 rounded-md border-[3px] border-kirana-brown p-1 sm:gap-1 sm:p-2 ${active ? "zone-glow" : "hard-shadow-sm"}`
        }`}
        style={isShelf ? undefined : { background: zone.color }}
      >
        {isShelf ? (
          <ShelfDisplay zone={zone} unlocked={unlocked} count={count} />
        ) : (
          <>
            <div className={`flex items-center justify-center rounded bg-black/15 ${isDoor || isNpc ? "h-9 w-9 sm:h-12 sm:w-12" : "h-10 w-12 sm:h-14 sm:w-16"}`}>
              <Icon className="h-5 w-5 text-white drop-shadow sm:h-7 sm:w-7" strokeWidth={2.2} />
            </div>
            <span className="font-signboard max-w-[5rem] text-center text-[9px] font-bold leading-tight text-white drop-shadow sm:max-w-[7rem] sm:text-[11px]">
              {zone.label}
            </span>
          </>
        )}
      </button>
    </div>
  );
};
